/**
 * CategoriasFinanceirasPage Component
 * 
 * Página de listagem e gerenciamento de categorias financeiras.
 * Permite visualizar, criar, editar e excluir categorias de receitas e despesas.
 * 
 * Funcionalidades:
 * - Listagem hierárquica de categorias
 * - Filtros por tipo (receita/despesa)
 * - Modal de criação/edição
 * - Suporte a hierarquia (categorias e subcategorias)
 */

import { useState, useEffect, useCallback } from 'react'
import { Edit, Trash2, Plus, Search } from 'lucide-react'
import DataTable from '../../../../components/ERP/DataTable/DataTable'
import Modal from '../../../../components/ERP/Modal/Modal'
import FormField from '../../../../components/ERP/Form/FormField'
import { financialCategoryService } from '../../../../services/erpApi'
import './CategoriasFinanceirasPage.css'

function CategoriasFinanceirasPage() {
  const [categorias, setCategorias] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingCategoria, setEditingCategoria] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    type: 'revenue',
    parentId: null,
    active: true,
    description: '',
    excludeFromDre: false,
    countAsDreRevenue: false
  })
  const [errors, setErrors] = useState({})
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    active: ''
  })

  const loadCategorias = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Filtrar apenas os parâmetros que têm valor
      const cleanFilters = {}
      if (filters.search && filters.search.trim()) {
        cleanFilters.search = filters.search.trim()
      }
      if (filters.type) {
        cleanFilters.type = filters.type
      }
      if (filters.active !== '') {
        cleanFilters.active = filters.active
      }
      
      const response = await financialCategoryService.getAll(cleanFilters)
      if (response && response.success) {
        setCategorias(response.data || [])
      } else {
        const errorMessage = response?.message || 'Erro ao carregar categorias'
        setError(errorMessage)
        setCategorias([])
        console.error('Resposta inválida da API:', response)
      }
    } catch (error) {
      console.error('Erro ao carregar categorias financeiras:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao carregar categorias financeiras'
      setError(errorMessage)
      setCategorias([])
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadCategorias()
  }, [loadCategorias])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    try {
      const dataToSend = {
        name: formData.name,
        type: formData.type,
        parentId: formData.parentId || null,
        active: formData.active,
        description: formData.description || null,
        excludeFromDre: formData.excludeFromDre,
        countAsDreRevenue: formData.type === 'revenue' && formData.countAsDreRevenue === true
      }

      let response
      if (editingCategoria) {
        response = await financialCategoryService.update(editingCategoria.id, dataToSend)
      } else {
        response = await financialCategoryService.create(dataToSend)
      }

      if (response.success) {
        setModalOpen(false)
        resetForm()
        loadCategorias()
      } else {
        if (response.errors && Array.isArray(response.errors)) {
          const errorObj = {}
          response.errors.forEach(err => {
            errorObj[err.param || err.path] = err.msg || err.message
          })
          setErrors(errorObj)
        } else {
          setErrors({ submit: response.message || 'Erro ao salvar categoria' })
        }
      }
    } catch (error) {
      console.error('Erro ao salvar categoria:', error)
      const errorMessage = error.response?.data?.message || 'Erro ao salvar categoria'
      const errorDetails = error.response?.data?.errors
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const errorObj = {}
        errorDetails.forEach(err => {
          errorObj[err.param || err.path] = err.msg || err.message
        })
        setErrors(errorObj)
      } else {
        setErrors({ submit: errorMessage })
      }
    }
  }

  const handleEdit = (categoria) => {
    setEditingCategoria(categoria)
    setFormData({
      name: categoria.name || '',
      type: categoria.type || 'revenue',
      parentId: categoria.parent_id || null,
      active: categoria.active !== undefined ? categoria.active : true,
      description: categoria.description || '',
      excludeFromDre: categoria.exclude_from_dre !== undefined ? categoria.exclude_from_dre : false,
      countAsDreRevenue: categoria.count_as_dre_revenue === true
    })
    setModalOpen(true)
  }

  const handleDelete = async (categoria) => {
    if (window.confirm(`Deseja realmente excluir a categoria "${categoria.name}"?`)) {
      try {
        const response = await financialCategoryService.delete(categoria.id)
        if (response.success) {
          loadCategorias()
        } else {
          alert(response.message || 'Erro ao excluir categoria')
        }
      } catch (error) {
        console.error('Erro ao excluir categoria:', error)
        alert('Erro ao excluir categoria')
      }
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'revenue',
      parentId: null,
      active: true,
      description: '',
      excludeFromDre: false,
      countAsDreRevenue: false
    })
    setEditingCategoria(null)
    setErrors({})
  }

  const getParentCategories = () => {
    return categorias.filter(c => !c.parent_id && c.id !== editingCategoria?.id)
  }

  const columns = [
    { key: 'name', label: 'Nome', sortable: true },
    {
      key: 'type',
      label: 'Tipo',
      render: (value) => {
        const typeMap = {
          revenue: { label: 'Receita', class: 'revenue' },
          expense: { label: 'Despesa', class: 'expense' }
        }
        const type = typeMap[value] || typeMap.revenue
        return <span className={`type-badge ${type.class}`}>{type.label}</span>
      }
    },
    {
      key: 'parent',
      label: 'Categoria Pai',
      render: (value, row) => {
        if (row.parent_id) {
          const parent = categorias.find(c => c.id === row.parent_id)
          return parent ? parent.name : '-'
        }
        return '-'
      }
    },
    {
      key: 'active',
      label: 'Status',
      render: (value) => {
        return value ? (
          <span className="status-badge active">Ativa</span>
        ) : (
          <span className="status-badge inactive">Inativa</span>
        )
      }
    },
    {
      key: 'exclude_from_dre',
      label: 'Excluída do DRE',
      render: (value, row) => {
        if (row.type === 'expense' && value) {
          return <span className="status-badge" style={{ background: '#fef3c7', color: '#92400e' }}>Sim</span>
        }
        return <span style={{ color: '#94a3b8' }}>-</span>
      }
    },
    {
      key: 'count_as_dre_revenue',
      label: 'Receita no DRE',
      render: (value, row) => {
        if (row.type === 'revenue' && value) {
          return <span className="status-badge active">Sim</span>
        }
        return <span style={{ color: '#94a3b8' }}>-</span>
      }
    }
  ]

  const actions = [
    {
      label: 'Editar',
      icon: Edit,
      variant: 'primary',
      onClick: handleEdit
    },
    {
      label: 'Excluir',
      icon: Trash2,
      variant: 'danger',
      onClick: handleDelete
    }
  ]

  return (
    <div className="categorias-financeiras-page">
      <div className="page-header">
        <div>
          <h1>Categorias Financeiras</h1>
          <p>Gerencie as categorias de receitas e despesas</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            resetForm()
            setModalOpen(true)
          }}
        >
          <Plus size={18} />
          Nova Categoria
        </button>
      </div>

      <div className="page-filters">
        <div className="filters-row">
          <div className="search-input-wrapper">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar categorias..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="search-input"
            />
          </div>
          <select
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            className="filter-select"
          >
            <option value="">Todos os tipos</option>
            <option value="revenue">Receita</option>
            <option value="expense">Despesa</option>
          </select>
          <select
            value={filters.active}
            onChange={(e) => setFilters({ ...filters, active: e.target.value })}
            className="filter-select"
          >
            <option value="">Todos os status</option>
            <option value="true">Ativas</option>
            <option value="false">Inativas</option>
          </select>
        </div>
      </div>

      {error && (
        <div style={{ 
          background: '#fee2e2', 
          color: '#991b1b', 
          padding: '1rem', 
          borderRadius: '8px', 
          marginBottom: '1rem' 
        }}>
          <strong>Erro:</strong> {error}
          <button 
            onClick={loadCategorias}
            style={{ 
              marginLeft: '1rem', 
              padding: '0.5rem 1rem', 
              background: '#991b1b', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer' 
            }}
          >
            Tentar novamente
          </button>
        </div>
      )}

      <DataTable
        columns={columns}
        data={categorias}
        loading={loading}
        actions={actions}
        emptyMessage="Nenhuma categoria financeira encontrada"
      />

      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          resetForm()
        }}
        title={editingCategoria ? 'Editar Categoria Financeira' : 'Nova Categoria Financeira'}
        size="medium"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => {
                setModalOpen(false)
                resetForm()
              }}
            >
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
            >
              {editingCategoria ? 'Salvar' : 'Criar'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <FormField
            label="Nome"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={errors.name}
            required
          />

          <FormField
            label="Tipo"
            name="type"
            type="select"
            value={formData.type}
            onChange={(e) => setFormData({
              ...formData,
              type: e.target.value,
              countAsDreRevenue: e.target.value === 'revenue' ? formData.countAsDreRevenue : false
            })}
            error={errors.type}
            options={[
              { value: 'revenue', label: 'Receita' },
              { value: 'expense', label: 'Despesa' }
            ]}
            required
          />

          <FormField
            label="Categoria Pai"
            name="parentId"
            type="select"
            value={formData.parentId || ''}
            onChange={(e) => setFormData({ ...formData, parentId: e.target.value || null })}
            error={errors.parentId}
            options={[
              { value: '', label: 'Nenhuma (categoria principal)' },
              ...getParentCategories().map(c => ({ value: c.id, label: c.name }))
            ]}
          />

          <FormField
            label="Descrição"
            name="description"
            type="textarea"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            error={errors.description}
          />

          <FormField
            label="Status"
            name="active"
            type="select"
            value={formData.active ? 'true' : 'false'}
            onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
            error={errors.active}
            options={[
              { value: 'true', label: 'Ativa' },
              { value: 'false', label: 'Inativa' }
            ]}
          />

          {formData.type === 'revenue' && (
            <div className="form-field">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="countAsDreRevenue"
                  checked={formData.countAsDreRevenue === true}
                  onChange={(e) => setFormData({ ...formData, countAsDreRevenue: e.target.checked })}
                  style={{ width: 'auto', margin: 0 }}
                />
                <span>Constar como receita no DRE</span>
              </label>
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#64748b', fontSize: '0.875rem' }}>
                Se marcado, os lançamentos dessa categoria entram em Receita Operacional Líquida.
              </small>
            </div>
          )}

          {formData.type === 'expense' && (
            <div className="form-field">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="excludeFromDre"
                  checked={formData.excludeFromDre}
                  onChange={(e) => setFormData({ ...formData, excludeFromDre: e.target.checked })}
                  style={{ width: 'auto', margin: 0 }}
                />
                <span>Excluir do DRE</span>
              </label>
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#64748b', fontSize: '0.875rem' }}>
                Se marcado, esta categoria será excluída do cálculo do DRE (Demonstração do Resultado do Exercício)
              </small>
              {errors.excludeFromDre && (
                <span className="form-error">{errors.excludeFromDre}</span>
              )}
            </div>
          )}

          {errors.submit && (
            <div className="form-error-message">{errors.submit}</div>
          )}
        </form>
      </Modal>
    </div>
  )
}

export default CategoriasFinanceirasPage

